import {Component, computed, inject, Signal, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ThirdPartyComponent} from '../../third-party.component';
import {
  ThirdPartyLicenseManagementAnswer
} from '../../third-party-license-management/third-party-license-management.service';
import {NotificationService} from '@uofu-uss/angular-util';

export type DesiredApps = {
  'Acrobat Pro': boolean,
  'Photoshop': boolean,
  'Illustrator': boolean,
  'InDesign': boolean,
  'Premiere Pro': boolean,

  'Other': boolean,
  'OtherText': string,
};

export function emptyDesiredApps(): DesiredApps {
  return {
    'Acrobat Pro': false,
    Photoshop: false,
    Illustrator: false,
    InDesign: false,
    'Premiere Pro': false,
    Other: false,
    OtherText: '',
  };
}

@Component({
  selector: 'app-adobe-cc-request',
  standalone: true,
  imports: [
    FormsModule,
    ThirdPartyComponent
  ],
  templateUrl: './adobe-cc-request.component.html',
  styleUrl: './adobe-cc-request.component.scss'
})
export class AdobeCcRequestComponent {
  private readonly notificationService = inject(NotificationService);

  readonly question1 =
    'Which specific Adobe applications do you require?';
  readonly question2 =
    'Provide a brief justification for why you require an Adobe Creative Cloud All Apps Pro License:';
  readonly vendorName = 'adobe';
  readonly name = 'creative cloud';

  adobeApplications: ReadonlyArray<keyof DesiredApps> = [
    'Acrobat Pro',
    'Photoshop',
    'Illustrator',
    'InDesign',
    'Premiere Pro',
  ];

  /* Question:
   * Which specific Adobe applications do you require? */
  readonly desiredApps: WritableSignal<DesiredApps> = signal<DesiredApps>(emptyDesiredApps());

  /* Question:
   * Please describe the specific course/project requirements that cannot be accomplished using Adobe Express Premium: */
  readonly justification: WritableSignal<string> = signal<string>('');

  readonly answers: Signal<ReadonlyArray<ThirdPartyLicenseManagementAnswer>> = computed(() => {
    const desiredApps = this.desiredApps();
    const justification = this.justification();
    return this.mapToAnswers(this.desiredAppsAsList(desiredApps), justification);
  });

  get validator(): () => boolean {
    return this.validate.bind(this);
  }

  updateDesiredApps(app: keyof DesiredApps, input: boolean | string): void {
    const desiredApps = this.desiredApps();
    this.desiredApps.set({
      ...desiredApps,
      [app]: input,
    });
  }

  desiredAppsAsList(desiredApps: DesiredApps): ReadonlyArray<string> {
    const selected: string[] = Object.keys(desiredApps)
      .filter((key: keyof DesiredApps) => key !== 'Other' && key !== 'OtherText')
      .filter((key: keyof DesiredApps) => desiredApps[key] === true);

    if (desiredApps.Other && desiredApps.OtherText) {
      selected.push(`Other: ${desiredApps.OtherText}`);
    }

    return selected;
  }

  mapToAnswers(desiredApps: ReadonlyArray<string>,
               justification: string): ReadonlyArray<ThirdPartyLicenseManagementAnswer> {
    const tplmAnswer1 = {
      question: this.question1,
      answer: desiredApps,
    };

    const tplmAnswer2 = {
      question: this.question2,
      answer: [justification],
    };

    return [tplmAnswer1, tplmAnswer2];
  }

  validate(): boolean {
    this.notificationService.clear();
    const desiredAppsList = this.desiredAppsAsList(this.desiredApps());
    const justification = this.justification();
    const desiredAppsValid = !!(desiredAppsList.length);
    const justificationValid = !!justification;
    if (!desiredAppsValid) {
      this.notificationService.addWarning({
        body: 'You must select at least one desired application.',
        focus: 'desiredApps',
        title: 'Unable to request access',
        alternateFocusDescription: 'Jump to the application options',
      });
    }
    if (!justificationValid) {
      this.notificationService.addWarning({
        body: 'You must describe your project requirements.',
        focus: 'justification',
        title: 'Unable to request access',
        alternateFocusDescription: 'Jump to the project requirements description',
      });
    }

    return desiredAppsValid && justificationValid;
  }
}
